/**
 * Trace Studio Panel Implementation — panel shell (tab bar, team picker,
 * account menu, refresh button). The tab contents live in src/catalog-tabs/.
 *
 * Split out from catalog-panel.tsx so that Vite HMR can hot-reload changes
 * without requiring a full page reload (the inspector captures component
 * references at init time and never re-requests them). Edits to this file or
 * any of the tab modules bubble up to the HMR boundary in catalog-panel.tsx.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Teams } from 'trace-viewer/cms';
import type { UserTeamResponseDto } from 'trace-viewer/cms';
import {
    styles,
    apiConfig,
    LoginScreen,
    getCatalogActiveTab,
    setCatalogActiveTab,
} from './catalog-shared';
import {
    getCatalogToken, onCatalogTokenChange,
    getCatalogEmail, handleLoginSuccess, handleLogout as doLogout,
    getCatalogSelectedTeamId,
    authReady,
} from './catalog-auth';
import { cachedSlot, useMirrorSlot, useSharedTeam, getCatalogRefreshHandler } from './catalog-tabs/state';
import { CatalogTab } from './catalog-tabs/catalog';
import { MyContentTab } from './catalog-tabs/my-content';
import { ProjectsTab } from './catalog-tabs/projects';

type Tab = 'catalog' | 'myContent' | 'projects';

export const CatalogPanelImpl: React.FC = () => {
    const [token, setToken] = useState<string | null>(getCatalogToken);
    const [email, setEmail] = useState<string | null>(getCatalogEmail);
    const [activeTab, setActiveTab_] = useState<Tab>(getCatalogActiveTab());
    const setActiveTab = useCallback((tab: Tab) => { setActiveTab_(tab); setCatalogActiveTab(tab); }, []);

    const handleLogin = useCallback((accessToken: string, userEmail: string) => {
        handleLoginSuccess(accessToken, userEmail);
        setToken(accessToken);
        setEmail(userEmail);
    }, []);

    const handleLogout = useCallback(() => {
        doLogout();
        setToken(null);
        setEmail(null);
    }, []);

    const [isRefreshing, setIsRefreshing] = useState(false);
    const handleRefresh = useCallback(() => {
        if (isRefreshing) return;
        const handler = getCatalogRefreshHandler();
        if (!handler) return;
        setIsRefreshing(true);
        // Floor the spinner duration so fast refreshes still register visually.
        const MIN_SPIN_MS = 400;
        const start = Date.now();
        void Promise.resolve(handler()).finally(() => {
            const remaining = MIN_SPIN_MS - (Date.now() - start);
            if (remaining > 0) setTimeout(() => setIsRefreshing(false), remaining);
            else setIsRefreshing(false);
        });
    }, [isRefreshing]);

    // Shared team list + selection. Lifted into the parent so the picker can
    // live in the panel header and every tab observes the same value.
    const [teams, setTeams] = useState<UserTeamResponseDto[]>(() => cachedSlot('shared.teams', []));
    useMirrorSlot('shared.teams', teams);
    const [selectedTeamId, setSelectedTeamId] = useSharedTeam();

    // Fetch teams once after login (and re-fetch if the token changes).
    useEffect(() => {
        if (!token) return;
        if (teams.length > 0) return;
        void (async () => {
            try {
                const client = new Teams(apiConfig(token));
                const res = await client.teamsList();
                const list = res.data ?? [];
                setTeams(list);
                // If no team is selected yet, default to the stored one if it
                // still exists, otherwise the first team in the list.
                if (!getCatalogSelectedTeamId() && list.length > 0) {
                    setSelectedTeamId(list[0].teamId ?? null);
                }
            } catch (err) {
                console.error('[CatalogPanel] Failed to fetch teams:', err);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Subscribe to external token changes (e.g. eager init completing after mount)
    useEffect(() => {
        // If eager init already resolved before this component mounted, sync up
        void authReady.then(() => {
            const t = getCatalogToken();
            if (t) {
                setToken(t);
                setEmail(getCatalogEmail());
            }
        });
        return onCatalogTokenChange((t) => {
            setToken(t);
            setEmail(t ? getCatalogEmail() : null);
        });
    }, []);

    if (!token) {
        return <LoginScreen onLogin={handleLogin} />;
    }

    // Keep all three tabs mounted so navigation state (selected folder, search,
    // scroll, etc.) is preserved when the user toggles between tabs. The
    // inactive panes are hidden with display:none.
    const paneStyle = (active: boolean): React.CSSProperties => ({
        display: active ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
    });

    return (
        <div style={styles.container}>
            <div style={styles.tabBar}>
                <button style={styles.tab(activeTab === 'myContent')} onClick={() => setActiveTab('myContent')}>My Content</button>
                <button style={styles.tab(activeTab === 'catalog')} onClick={() => setActiveTab('catalog')}>Catalog</button>
                <button style={styles.tab(activeTab === 'projects')} onClick={() => setActiveTab('projects')}>Projects</button>
                <div style={{ flex: 1 }} />
                <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    title={isRefreshing ? 'Refreshing...' : 'Refresh current view'}
                    aria-label="Refresh"
                    style={{
                        background: 'none', border: 'none',
                        cursor: isRefreshing ? 'default' : 'pointer',
                        color: 'var(--colorNeutralForeground3)', padding: '4px 6px',
                        alignSelf: 'center', fontSize: '14px', lineHeight: 1, borderRadius: '3px',
                        display: 'inline-block',
                    }}
                >
                    <span style={{
                        display: 'inline-block',
                        animation: isRefreshing ? 'spin 0.8s linear infinite' : undefined,
                    }}>&#x21bb;</span>
                </button>
            </div>
            <div style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                marginBottom: '6px', flexShrink: 0,
            }}>
                {teams.length > 0 && (
                    <select
                        value={selectedTeamId ?? ''}
                        onChange={e => setSelectedTeamId(e.target.value || null)}
                        disabled={teams.length === 1}
                        style={{
                            ...styles.select,
                            flex: 1, minWidth: 0, marginBottom: 0,
                            fontSize: '11px', padding: '3px 6px',
                            cursor: teams.length === 1 ? 'default' : 'pointer',
                            opacity: teams.length === 1 ? 0.7 : 1,
                        }}
                        title={teams.length === 1 ? 'Active team' : 'Switch team'}
                    >
                        {teams.map(t => (
                            <option key={t.teamId} value={t.teamId ?? ''}>{t.name ?? 'Unnamed Team'}</option>
                        ))}
                    </select>
                )}
                <select
                    value=""
                    onChange={e => { if (e.target.value === 'logout') handleLogout(); e.target.value = ''; }}
                    style={{
                        ...styles.select,
                        flex: 1, minWidth: 0, marginBottom: 0,
                        fontSize: '11px', padding: '3px 6px', cursor: 'pointer',
                    }}
                    title={email ?? undefined}
                >
                    <option value="" disabled hidden>{email ?? 'Account'}</option>
                    <option value="logout">Sign Out</option>
                </select>
            </div>
            <div style={paneStyle(activeTab === 'myContent')}>
                <MyContentTab token={token} active={activeTab === 'myContent'} />
            </div>
            <div style={paneStyle(activeTab === 'projects')}>
                <ProjectsTab token={token} active={activeTab === 'projects'} />
            </div>
            <div style={paneStyle(activeTab === 'catalog')}>
                <CatalogTab token={token} active={activeTab === 'catalog'} />
            </div>
        </div>
    );
};
